/**
 * buildSkinCss — expand a ThemeConfig into a CSS string that overrides every shell
 * design token (globals.css `:root`) plus structural rules (background, glass, radius,
 * density). Scoped to `:root[data-skin="<id>"]` so it wins over the base `:root`/`.dark`
 * (higher specificity) and only applies when <html data-skin="<id>"> is set.
 *
 * Pure + deterministic → unit-tested in tests/whitelabel-skin-css.test.ts. The AI
 * system-designer (Phase 4) edits ThemeConfig objects; this function is the one place
 * that knows how a semantic config maps to the concrete platform variables.
 */
import { readableInk } from "./contrast";
import type { ThemeConfig } from "./skins";
import { flatChromeCss, minimalChromeCss, shellChromeCss } from "./shell-skins";

/** The skins that ARE the house look. Their onboarding stays the hand-tuned
 *  literals in onboarding.css rather than a derivation that approximates them
 *  (premium's primary is the same navy, but a 52% mix toward near-black is
 *  not the same colour as the navy itself). */
const HOUSE_SKIN_IDS = new Set(["default", "premium"]);

/** `color-mix` shorthand: `pct`% of `color` over `over`. */
function mix(color: string, pct: number, over = "transparent"): string {
  return `color-mix(in srgb, ${color} ${pct}%, ${over})`;
}

/** The `--var: value;` declarations (no selector) for a skin's tokens. */
export function skinVarDeclarations(c: ThemeConfig): string {
  const sidebarBg = c.sidebarBg ?? c.surface2;
  const sidebarFg = c.sidebarFg ?? c.fg;
  const danger = c.danger ?? "#e5484d";
  const gold = c.gold ?? "#c9a24b";
  const success = c.success ?? "#2fa36b";
  const warning = c.warning ?? "#d69e2e";
  // `?.length ? :` not `??`: an EXPLICITLY empty charts array (an AI-authored
  // theme clearing customization is a plausible way to get one) must fall
  // back the same way unset does — `??` only catches null/undefined, so an
  // empty array slipped through as five `--chart-N: undefined;` declarations,
  // the one place in this file that broke the "empty = default" convention
  // every other whitelabel array field (moduleSubset, pinnedApps, allowedModules) uses.
  const charts = c.charts?.length ? c.charts : [c.primary, c.ring, "#c9a24b", "#2fa36b", "#7c5cd6"];
  const decls: Record<string, string> = {
    // core
    "--background": c.bg,
    "--foreground": c.fg,
    "--card": c.surface,
    "--card-foreground": c.fg,
    "--popover": c.surface,
    "--popover-foreground": c.fg,
    // brand / primary
    "--brand": c.primary,
    "--brand-foreground": c.primaryFg,
    "--brand-muted": mix(c.primary, 14),
    "--primary": c.primary,
    "--primary-foreground": c.primaryFg,
    "--sky": c.ring,
    // Semantic hues were hardcoded here, so a brand could restyle everything
    // EXCEPT its own success/warning/accent — the three most likely to clash,
    // because a brand that is itself green ends up with two nearly-identical
    // greens meaning different things on the same screen.
    "--gold": gold,
    "--success": success,
    "--warning": warning,
    // A semantic colour has two jobs and one value: it FILLS a badge and it
    // LABELS one, and those want different lightnesses. Every shipped skin
    // measured 2.1-3.3:1 using the fill value as the label. Deriving the ink means
    // a brand picks one colour and the readable variant follows, rather than being
    // told its green is wrong. Matches the *-ink tokens globals.css already
    // defines for the house palette — before this, a brand that changed `success`
    // still got the HOUSE ink, because buildSkinCss never emitted these.
    "--success-ink": readableInk(success, c.surface),
    "--warning-ink": readableInk(warning, c.surface),
    "--destructive-ink": readableInk(danger, c.surface),
    "--gold-ink": readableInk(gold, c.surface),
    // --sky (line above) is the skin's own `ring` colour, which genuinely
    // diverges per skin (unlike gold, which is close to universal) — text-sky-ink
    // is used widely (onboarding, offer badges, admin billing), so a brand
    // whose ring diverges from the house sky hue was showing the HOUSE ink
    // regardless of what --sky it actually set. Same readableInk pattern as
    // the other four; buildSkinCss simply never emitted this one.
    "--sky-ink": readableInk(c.ring, c.surface),
    // neutrals
    // TYPOGRAPHY. ThemeConfig.font/headingFont have existed since the type was
    // written, every skin sets them, the AI designer can write them — and
    // buildSkinCss never emitted a font-family, so the product was Inter for
    // everyone whatever a brand chose. --font-sans is defined by next/font as a
    // CLASS on <html> (specificity 0,1,0); these rules are scoped
    // :root[data-skin="..."] (0,2,0) and therefore win, so a brand overrides the
    // default without the layout needing to know brands exist.
    "--font-sans": c.font,
    // --brand-font-heading, not --font-heading: the latter is consumed by
    // Tailwind's `@theme inline` block, which bakes its value at build time and
    // would never see a runtime override. See the note in globals.css.
    "--brand-font-heading": c.headingFont ?? c.font,
    "--secondary": c.surface2,
    "--secondary-foreground": c.fg,
    "--muted": c.surface2,
    "--muted-foreground": c.muted,
    "--accent": c.surface2,
    "--accent-foreground": c.fg,
    "--destructive": danger,
    "--border": c.border,
    "--input": c.inputBorder ?? c.border,
    "--ring": c.ring,
    "--radius": c.radius,
    // §3.7 layout — sidebar.tsx reads --brand-sidebar-width via var() with a
    // 16rem fallback (its own hardcoded default), so a brand that doesn't set
    // railWidth renders identically to before this existed. contentMaxWidth
    // same shape, consumed by (app)/layout.tsx's content wrapper.
    "--brand-sidebar-width": c.railWidth || "16rem",
    "--brand-content-max-width": c.contentMaxWidth || "none",
    // charts
    "--chart-1": charts[0],
    "--chart-2": charts[1] ?? charts[0],
    "--chart-3": charts[2] ?? charts[0],
    "--chart-4": charts[3] ?? charts[0],
    "--chart-5": charts[4] ?? charts[0],
    // ── SHAPE (docs/agent-runtime-and-design-ai.md §3.3) ─────────────────
    // A form language, expressible at last. Each falls back to the house
    // value in CSS via `var(--wl-shape-x, <house>)`, so an edition that sets
    // none renders exactly as before — which is why these are emitted for
    // every skin rather than gated the way the onboarding anchors are.
    ...(c.control?.radius ? { "--wl-btn-radius": c.control.radius } : {}),
    ...(c.control?.case ? { "--wl-btn-transform": c.control.case === "upper" ? "uppercase" : "none" } : {}),
    ...(c.control?.tracking ? { "--wl-btn-tracking": c.control.tracking } : {}),
    ...(c.control?.weight ? { "--wl-btn-weight": c.control.weight } : {}),
    ...(c.control?.shadow === false ? { "--wl-btn-shadow": "none" } : {}),
    // A BOX field is the whole set at once — border, radius, fill and padding
    // — because "boxed" is not one declaration, it is a coherent treatment.
    // Underline is the default and needs no tokens at all.
    ...(c.field?.style === "box"
      ? {
          "--wl-field-border": `1px solid ${mix(c.fg, 18)}`,
          "--wl-field-underline-rule": `1px solid ${mix(c.fg, 18)}`,
          "--wl-field-radius": c.field.radius ?? c.radius,
          "--wl-field-bg": c.field.fill === "transparent" ? "transparent" : mix(c.surface, 60),
          "--wl-field-padding": "11px 13px",
        }
      : c.field?.radius
        ? { "--wl-field-radius": c.field.radius }
        : {}),
    ...(c.panel?.radius ? { "--wl-panel-radius": c.panel.radius } : {}),
    ...(c.panel?.border ? { "--wl-panel-border": c.panel.border } : {}),
    ...(c.heading?.weight ? { "--wl-heading-weight": c.heading.weight } : {}),
    ...(c.heading?.tracking ? { "--wl-heading-tracking": c.heading.tracking } : {}),
    ...(c.eyebrow?.size ? { "--wl-eyebrow-size": c.eyebrow.size } : {}),
    ...(c.eyebrow?.tracking ? { "--wl-eyebrow-tracking": c.eyebrow.tracking } : {}),
    // sidebar
    "--sidebar": sidebarBg,
    "--sidebar-foreground": sidebarFg,
    "--sidebar-primary": c.primary,
    "--sidebar-primary-foreground": c.primaryFg,
    "--sidebar-accent": mix(sidebarFg, 10, sidebarBg),
    "--sidebar-accent-foreground": sidebarFg,
    "--sidebar-border": mix(sidebarFg, 14, sidebarBg),
    "--sidebar-ring": c.ring,
    // ── ONBOARDING (the /onboarding cinematic surface) ────────────────────
    // GATED BY IDENTITY, NOT BY data-skin ABSENCE. The obvious design —
    // "no skin, no vars, fallbacks render the house" — does not survive
    // contact with ssrSkinFromCookies, which maps an ABSENT cookie to the
    // `premium` skin: data-skin is on <html> for essentially every visitor,
    // house included. Verified in the browser: with premium emitting these,
    // the house door's navy became mix(#1f4e79 52%, near-black) — visibly
    // darker than its own oklch literal. So the two house-identity skins emit
    // nothing here and the onboarding.css fallbacks carry them, exactly.
    // These exist because the ordinary tokens above CANNOT reach onboarding:
    // its layout forces a `.dark` class on the surface's own root element, and
    // globals.css's `.dark {}` block re-declares --background/--card/--foreground
    // ON that element — which, custom properties resolving from the nearest
    // ancestor, beats anything a skin declares up on <html>. So a branded
    // visitor's first screen rendered in the HOUSE navy and sky whatever their
    // skin said. New names are the fix: `.dark` does not re-declare these, so
    // they inherit straight through it, and onboarding.css consumes them as
    // `var(--wl-ob-*, <house literal>)` — no skin, no vars, house unchanged.
    //
    // DERIVED from the palette rather than five new author-facing fields: every
    // existing skin and every AI-authored theme gets a coherent onboarding for
    // free, and the design console's existing controls (primary/ring/gold)
    // already steer it. The surface stays a dark cinematic field by design —
    // (onboarding/layout.tsx forces dark for the same reason the auth screen
    // does) — so light skins contribute their HUES, mixed down onto a near-
    // black base, rather than their light canvas.
    ...(HOUSE_SKIN_IDS.has(c.id)
      ? {}
      : {
          // The MIX RATIOS are low on purpose. The field paints these as huge
          // radial pools at 70-85% opacity, so whatever goes in here covers
          // most of the screen. The house gets away with 85% because its navy
          // is already dark (oklch L=0.41); a brand whose primary is BRIGHT —
          // a safety amber, a lime — flooded the whole canvas and turned a
          // cinematic dark field into a flat wash (seen in the browser at 52%:
          // the dealer door went uniformly muddy orange).
          //
          // Mixing the brand DOWN toward near-black first means the pools stay
          // dark and the brand reads as the tint of the light rather than the
          // light itself, for a bright primary and a dark one alike.
          "--wl-ob-deep": mix(c.primary, 30, "#060a12"),    // the pool color (house: navy)
          // NOT darkened, unlike --wl-ob-deep above. The field's own gradient
          // stops already dilute this one heavily (42% and 22% alpha), and it
          // doubles as --brand/--sky inside the surface — the colour of the
          // primary BUTTON and the links. Pre-darkening it turned the CTA
          // brown, which is the opposite of the problem being fixed: `deep`
          // floods because it is painted at 70-85% alpha, `light` does not.
          "--wl-ob-light": c.ring,                          // the light source (house: sky)
          "--wl-ob-accent": gold,                           // the sparing accent (house: gold)
          "--wl-ob-canvas": mix(c.primary, 6, "#05070c"),   // the field's base under the pools
          "--wl-ob-surface": mix(c.primary, 14, "#0c1018"), // glass base (replaces --card, which .dark clobbers)
          "--wl-ob-radius": c.radius,                       // shape carries through too
          // Readable tint of the light source for text on the dark field —
          // the onboarding-side --sky-ink. Lightened rather than raw because
          // a saturated mid-tone ring reads fine as a fill and poorly as text
          // on near-black.
          "--wl-ob-light-ink": mix(c.ring, 68, "#ffffff"),
        }),
  };
  return Object.entries(decls)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join("\n");
}

/** Full stylesheet for one skin (vars + structural rules), scoped to its data-skin. */
export function buildSkinCss(c: ThemeConfig): string {
  const sel = `:root[data-skin="${c.id}"]`;
  const parts: string[] = [];

  // 1. token overrides (also apply under .dark so the mode class can't out-specify)
  parts.push(`${sel}, ${sel}.dark {\n${skinVarDeclarations(c)}\n}`);

  // 2. app background (gradient / image painted on the shell canvas, viewport-fixed)
  if (c.appBg) {
    parts.push(`${sel} body { background: ${c.appBg}; background-attachment: fixed; }`);
  }

  // 2b. reveal the appBg through the content area (transparent SidebarInset). Glass frosts
  // its surfaces (§3); a non-glass reveal (e.g. the meadow) keeps airy solid cards that float
  // over the scene with a soft tinted shadow.
  if (c.revealBg || c.glass) {
    parts.push(`${sel} [data-slot="sidebar-inset"] { background: transparent; }`);
  }
  if (c.revealBg && !c.glass) {
    parts.push(
      `${sel} [data-slot="card"], ${sel} .bg-card {\n` +
        `  border: 1px solid ${c.border};\n` +
        `  box-shadow: 0 10px 30px rgba(46,110,70,0.12);\n}`,
    );
  }

  // 3. glass — reveal the fixed aurora through the content + sidebar, frost every surface.
  // SidebarInset (main) and the topbar paint bg-background; make the content area transparent
  // so the body gradient shows, then frost the translucent cards/sidebar over it.
  if (c.glass) {
    // The sidebar rule is gated on the user's OWN flat/glass surface toggle
    // (data-surface, qod_surface cookie) — this skin being NAMED "glass" sets
    // its default (see resolve.ts's org-default wiring), it doesn't force the
    // effect regardless of what the user picked on their device. Every other
    // glass-listed surface (card/popover/dialog/etc., globals.css's :where()
    // list) already composes this way; the sidebar was the one slot outside
    // that shared list, so it was the one place a "flat" pick silently did
    // nothing. See docs/dark-glass-audit.md finding #1.
    parts.push(
      `${sel}[data-surface="glass"] [data-slot="sidebar"] {\n` +
        `  backdrop-filter: blur(22px) saturate(1.5);\n  -webkit-backdrop-filter: blur(22px) saturate(1.5);\n}`,
      `${sel} [data-slot="card"], ${sel} .bg-card, ${sel} [data-slot="popover-content"], ${sel} .bg-popover {\n` +
        `  backdrop-filter: blur(16px) saturate(1.5);\n` +
        `  -webkit-backdrop-filter: blur(16px) saturate(1.5);\n` +
        `  border: 1px solid rgba(255,255,255,0.6);\n` +
        `  box-shadow: 0 10px 34px rgba(31,26,60,0.10);\n}`,
    );
  }

  // 4. density — tighten the base rhythm for compact skins
  //
  // This emitted `--qod-density: compact` and nothing else for as long as the field
  // existed — a custom property with zero readers anywhere in the codebase (grep
  // confirms it), the same "declared, never wired" shape font-family was in before
  // that fix. A skin author sets density: "compact" (HANDYMAN_SKIN's own comment
  // even says "tight density"), it silently renders identically to comfortable.
  //
  // Real overrides on the nav chrome's own data-slot anchors. Unlayered vs
  // Tailwind's @layer utilities is what lets a plain [data-slot] selector beat a
  // static h-8/px-4 class regardless of source order — the same mechanism every
  // other rule in this file already relies on to beat the base :root tokens.
  if (c.density === "compact") {
    parts.push(
      `${sel} { --qod-density: compact; }`,
      `${sel} [data-slot="sidebar-menu-button"] { height: 1.75rem; padding-inline: 0.5rem; }`,
      `${sel} [data-slot="sidebar-group-label"] { height: 1.75rem; }`,
      `${sel} [data-slot="topbar"] { height: 3rem; }`,
      `${sel} [data-slot="sidebar-header"] > div { padding-block: 0.25rem; }`,
    );
  }

  // 5. vertical header — collapse the horizontal top bar into a seamless, borderless strip
  // so the vertical rail is the product's only structural "header" (Close-CRM style).
  if (c.headerLayout === "vertical") {
    parts.push(
      `${sel} [data-slot="topbar"] {\n` +
        `  border-bottom: 0;\n  background: var(--background);\n` +
        `  backdrop-filter: none;\n  -webkit-backdrop-filter: none;\n}`,
      `${sel} [data-slot="sidebar"] { border-right: 1px solid var(--sidebar-border); }`,
    );
  }

  // Hand-authored premium shell reskin (sidebar/topbar/cards/…), already scoped under
  // :root[data-skin="<id>"]. Appended last so it wins over the generated token rules.
  // The designed chrome, rendered in THIS brand's tokens. Previously this was a
  // lookup keyed by brand id with exactly one entry ("premium"), so a custom
  // brand — which resolve.ts re-ids to `wl-<id>` — matched nothing and got no
  // chrome whatsoever. That is what made "your own colours" and "a designed
  // shell" mutually exclusive. The other registry skins opt out: they are
  // distinct looks, not the premium look in another palette.
  const chrome = c.chrome ?? (c.id === "premium" || c.id.startsWith("wl-") ? "designed" : "plain");
  if (chrome === "designed") {
    const rich = shellChromeCss(c.id);
    if (rich) parts.push(rich);
  } else if (chrome === "flat") {
    const rich = flatChromeCss(c.id);
    if (rich) parts.push(rich);
  } else if (chrome === "minimal") {
    const rich = minimalChromeCss(c.id);
    if (rich) parts.push(rich);
  }

  // Backstop: this string is injected via dangerouslySetInnerHTML into a <style> tag
  // (layout.tsx, skin-provider.tsx). design-ops validates AI/admin-authored field values
  // before they're persisted, but this is the one chokepoint every ThemeConfig — old rows
  // written before that check existed, or a future caller — actually reaches. No legitimate
  // CSS value needs `<`, and stripping it is enough to make a `</style>` breakout impossible.
  return parts.join("\n\n").replace(/</g, "");
}

/** Build a `<style>` payload covering many skins at once (registry preload). */
export function buildSkinSheet(configs: ThemeConfig[]): string {
  return configs.map(buildSkinCss).join("\n\n");
}
